import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '../Input';
import { COLORS } from '@/utils/static';
import styled from 'styled-components';
import { dateString, getLocalizedText } from '@/utils/helpers';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components';
import { DatePicker as AntdDatePicker } from 'antd';
import ruRU from 'antd/es/date-picker/locale/ru_RU';
import lvLV from 'antd/es/date-picker/locale/lv_LV';
import enUS from 'antd/es/date-picker/locale/en_US';
import deDE from 'antd/es/date-picker/locale/de_DE';
import dayjs from 'dayjs';
import { CalendarOutlined } from '@ant-design/icons';

const tzOffset = new Date().getTimezoneOffset() * 60000; //timezone offset in milliseconds

const dpLocale = {
  en: enUS,
  de: deDE,
  lv: lvLV,
  ru: ruRU
};

export const DatePicker = forwardRef(
  (
    {
      label,
      selected,
      tooltip,
      tooltip_RU,
      tooltip_LV,
      onChange,
      disabled,
      required = false,
      error,
      showYearDropdown = false,
      minDate,
      maxDate,
      ...props
    },
    ref
  ) => {
    const { i18n } = useTranslation();
    const [showModal, setShowModal] = useState(false);

    const openModal = () => {
      !disabled && setShowModal(true);
    };

    const hideModal = useCallback((e) => {
      if (!e.target.closest('div[data-picker-modal]')) {
        setShowModal(false);
      }
    }, []);

    useEffect(() => {
      if (showModal) {
        document.addEventListener('click', hideModal);
      } else {
        document.removeEventListener('click', hideModal);
      }
    }, [showModal, hideModal]);

    const onSelect = (value) => {
      onChange(value ? dayjs(new Date(value - tzOffset)) : value);
      // setShowModal(false);
    };

    const datePickerLocale = useMemo(() => {
      return i18n?.language ? dpLocale[i18n.language] : enUS;
    }, [i18n]);

    return (
      <Wrapper data-picker-modal>
        {showYearDropdown && (
          <Label
            label={label}
            required={required}
            tooltip={
              tooltip
                ? getLocalizedText(
                    {
                      tooltip: tooltip,
                      tooltip_RU: tooltip_RU,
                      tooltip_LV: tooltip_LV
                    },
                    'tooltip',
                    i18n
                  )
                : null
            }
          >
            <StyledAntdDatePicker
              style={{ width: '100%' }}
              size='large'
              error={error}
              maxDate={dayjs(maxDate)}
              minDate={dayjs(minDate)}
              onChange={onSelect}
              disabled={props.adminView || disabled}
              locale={datePickerLocale}
              format={'DD.MM.YYYY'}
              defaultValue={selected === null ? undefined : dayjs(selected)}
              {...props}
            />
          </Label>
        )}

        {!showYearDropdown && (
          <div onClick={openModal} ref={ref}>
            <Input
              label={label}
              tooltip={tooltip}
              tooltip_RU={tooltip_RU}
              tooltip_LV={tooltip_LV}
              value={selected ? dateString(new Date(selected)) : ''}
              adornment={
                <CalendarOutlined
                  style={{
                    color: COLORS.iconColor
                  }}
                />
              }
              adornmentPosition={'end'}
              wide
              readOnly
              required={required}
              error={error ? error.message : ''}
            />
          </div>
        )}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  position: relative;
  cursor: pointer;

  input {
    cursor: pointer;
  }
  > .react-datepicker-wrapper {
    width: 100%;
  }
`;

const StyledAntdDatePicker = styled(AntdDatePicker)`
  padding: 9.5px 8px 9.5px 16px;
  height: 40px !important;
  font-size: 14px;
  color: #27272a;
  border-radius: 4px;
`;
